const HOST_SEL = '[data-professional-before-after]';
const TRACK_SEL = '.professional-case-study__compare-track';
const HANDLE_SEL = '.professional-case-study__compare-handle';

const NAME = 'ProfessionalBeforeAfter';

/** Use console.log so messages show with default DevTools levels (Logger uses console.info, often hidden). */
function dbg(...args) {
	console.log(`[${NAME}]`, ...args);
}

/**
 * Binds drag + keyboard control for the case-study before/after comparison.
 * Uses a data attribute instead of a custom element so it works when Shopify
 * injects section HTML (theme editor) and regardless of script timing.
 */
function bindBeforeAfter(host) {
	if (!host) {
		dbg('bind skipped (no host)');
		return;
	}
	if (host.dataset.beforeAfterBound === 'true') {
		dbg('bind skipped (already bound)', host);
		return;
	}

	const track = host.querySelector(TRACK_SEL);
	const handle = host.querySelector(HANDLE_SEL);
	if (!track) {
		console.warn(`[${NAME}] bind failed: missing track`, TRACK_SEL, host);
		return;
	}

	host.dataset.beforeAfterBound = 'true';
	dbg('binding', { hasHandle: !!handle });

	const abortController = new AbortController();
	const { signal } = abortController;

	let dragging = false;

	function setSplit(percent) {
		const pct = Math.max(0, Math.min(100, percent));
		const val = `${pct}%`;
		host.style.setProperty('--split', val);
		track.style.setProperty('--split', val);
		if (handle) {
			handle.setAttribute('aria-valuenow', String(Math.round(pct)));
		}
	}

	function percentFromClientX(clientX) {
		const rect = track.getBoundingClientRect();
		if (rect.width <= 0) return 50;
		const x = clientX - rect.left;
		return (x / rect.width) * 100;
	}

	function onPointerDown(e) {
		dbg('pointerdown', {
			button: e.button,
			pointerId: e.pointerId,
			pointerType: e.pointerType,
			target: e.target && e.target.className,
			clientX: e.clientX,
		});
		if (e.button !== undefined && e.button !== 0) return;
		dragging = true;
		try {
			track.setPointerCapture(e.pointerId);
			dbg('pointer capture OK', e.pointerId);
		} catch (err) {
			dbg('pointer capture failed (using document listeners)', err);
		}
		const pct = percentFromClientX(e.clientX);
		setSplit(pct);
		dbg('applied split from pointerdown', { pct });
	}

	function onPointerMove(e) {
		if (!dragging) return;
		e.preventDefault();
		setSplit(percentFromClientX(e.clientX));
	}

	function onPointerUp(e) {
		if (!dragging) return;
		dragging = false;
		dbg('pointerup / drag end', { pointerId: e.pointerId });
		try {
			track.releasePointerCapture(e.pointerId);
		} catch {
			/* ignore */
		}
	}

	function onKeyDown(e) {
		const step = e.shiftKey ? 10 : 3;
		const raw = host.style.getPropertyValue('--split').trim();
		let current = parseFloat(raw);
		if (!Number.isFinite(current)) {
			current = parseFloat(host.dataset.initialSplit);
			if (!Number.isFinite(current)) current = 50;
		}

		switch (e.key) {
			case 'ArrowLeft':
				e.preventDefault();
				setSplit(current - step);
				break;
			case 'ArrowRight':
				e.preventDefault();
				setSplit(current + step);
				break;
			case 'Home':
				e.preventDefault();
				setSplit(0);
				break;
			case 'End':
				e.preventDefault();
				setSplit(100);
				break;
			default:
				break;
		}
	}

	const initial = parseFloat(host.dataset.initialSplit);
	const startPct = Number.isFinite(initial) ? initial : 50;
	setSplit(startPct);

	track.addEventListener('pointerdown', onPointerDown, { signal });
	document.addEventListener('pointermove', onPointerMove, { signal });
	document.addEventListener('pointerup', onPointerUp, { signal });
	document.addEventListener('pointercancel', onPointerUp, { signal });

	if (handle) {
		handle.addEventListener('keydown', onKeyDown, { signal });
	}

	dbg('bound');
}

/**
 * @param {ParentNode} [root] - Search within this node (e.g. a section after theme editor load).
 */
function initProfessionalBeforeAfter(root) {
	const scope = root && root.querySelectorAll ? root : document;
	const scopeLabel =
		scope === document ? 'document' : scope.id || scope.className || scope.nodeName;
	try {
		const hosts = scope.querySelectorAll(HOST_SEL);
		dbg('init', {
			scope: scopeLabel,
			hostCount: hosts.length,
			readyState: document.readyState,
		});
		hosts.forEach(bindBeforeAfter);
	} catch (err) {
		console.error(`[${NAME}] init failed`, err);
	}
}

function onShopifySectionLoad(e) {
	const id = e.detail && e.detail.sectionId;
	const section = id ? document.getElementById(`shopify-section-${id}`) : null;
	initProfessionalBeforeAfter(section || document);
}

document.addEventListener('shopify:section:load', onShopifySectionLoad);

// Runs when lib/global is imported (page.js, product.js, etc.). Re-run on DOMContentLoaded + load
// so theme editor / late-injected markup is picked up if the first pass saw 0 hosts.
dbg('module loaded', {
	readyState: document.readyState,
	script: document.currentScript && document.currentScript.src,
});

function bootFromEvent(reason) {
	dbg('boot retry:', reason);
	initProfessionalBeforeAfter(document);
}

initProfessionalBeforeAfter(document);

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => bootFromEvent('DOMContentLoaded'), {
		once: true,
	});
}

window.addEventListener('load', () => bootFromEvent('window load'), { once: true });
